import { useState, useEffect, useRef, useCallback } from "react";
import PageLayout from "../components/PageLayout";
import { useParams, useNavigate, Link } from "react-router-dom";
import { FiSearch } from "react-icons/fi";
import IngredientAPI from "../api/ingredient";
import TradenameResultCard from "../components/tradenames/TradenameResultCard";
import Tabs from "../components/Tabs";
import Carousel from "../components/Carousel";
import SponsersAffiliations from "../components/SponsersAffiliations";
import { useSchemaMicrodata } from '../hooks/useSchemaMicrodata';
import { MdOutlineScience } from "react-icons/md";
import { RiCheckLine } from "react-icons/ri";
import { useIngredientSession } from "../hooks/useIngredientSession";

export default function IngredientItem() {
    const { uri } = useParams();
    const navigate = useNavigate();
    const { addIngredient, isInSession, sessionIngredients, clearSession, count, isFull } = useIngredientSession();
    const [ingredient, setIngredient] = useState(null);
    const [tradenames, setTradenames] = useState([]);
    const [topBrands, setTopBrands] = useState([]);
    const [wellReviewed, setWellReviewed] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, totalItems: 0, totalPages: 0 });
    const observer = useRef();

    // Add schema microdata for ingredient page
    useSchemaMicrodata('ingredient', {
      name: ingredient?.name,
      uri: uri,
      tradenames: tradenames,
      pagination: pagination
    }, [ingredient, tradenames, pagination]);

    const lastItemRef = useCallback(node => {
        if (isLoadingMore) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [isLoadingMore, hasMore]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const data = await IngredientAPI.getTradeNamesForIngredient(uri);
                const dataTopBrands = await IngredientAPI.getTopBrandsForIngredient(uri);
                const dataWellReviewed = await IngredientAPI.getWellReviewedForIngredient(uri);
                setIngredient(data.ingredient);
                setTradenames(data.tradenames);
                setTopBrands(dataTopBrands.topBrands ?? []);
                setWellReviewed(dataWellReviewed.wellReviewed ?? []);
                setPagination(data.pagination);
                setHasMore(data.pagination.page < data.pagination.totalPages);
            } catch (err) {
                setError('Failed to load ingredient details. Please try again later.');
                console.error('Error fetching ingredient:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [uri]);

    useEffect(() => {
        if (page === 1) return;

        const fetchMoreTradenames = async () => {
            try {
                setIsLoadingMore(true);
                const data = await IngredientAPI.getTradeNamesForIngredient(uri, page);

                setTradenames(prev => [...prev, ...data.tradenames]);
                setPagination(data.pagination);
                setHasMore(data.pagination.page < data.pagination.totalPages);
            } catch (err) {
                console.error('Error fetching more tradenames:', err);
            } finally {
                setIsLoadingMore(false);
            }
        };

        fetchMoreTradenames();
    }, [page, uri]);

    if (isLoading) {
        return (
            <PageLayout
                title=" "
                titleHighlight="Loading..."
                subtitle="Please wait while we fetch the ingredient details"
                pageTitle="Loading"
                showSearch={false}
                showGradient={true}
            >
                <div className="flex items-center justify-center h-64">
                    <div className="w-12 h-12 border-t-2 border-b-2 rounded-full animate-spin border-primary"></div>
                </div>
            </PageLayout>
        );
    }

    if (error || !ingredient) {
        return (
            <PageLayout
                title=" "
                titleHighlight="Error"
                subtitle="Failed to load ingredient details"
                pageTitle="Error"
                showSearch={false}
                showGradient={true}
            >
                <div className="p-6 text-center border shadow-sm bg-white/20 backdrop-blur-sm border-white/40 rounded-xl">
                    <div className="mb-4 text-red-500">{error || 'Ingredient not found'}</div>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 text-sm font-medium text-white rounded-md bg-primary hover:bg-primary/90"
                    >
                        Try Again
                    </button>
                </div>
            </PageLayout>
        );
    }

    const combineButton = (
        <button
            onClick={() => {
                if (isInSession(uri)) {
                    const uris = sessionIngredients.map((ing) => ing.uri).join(",");
                    navigate(`/ingredients/search?ingredients=${encodeURIComponent(uris)}`);
                    clearSession();
                } else {
                    addIngredient({ uri, name: ingredient.name });
                }
            }}
            disabled={isFull && !isInSession(uri)}
            className={`inline-flex items-center gap-2 px-5 py-2.5 text-15 font-text font-semibold rounded-xl active:scale-[0.98] transition-all duration-200 min-h-[44px] ${
                isInSession(uri)
                    ? "bg-emerald-700 hover:bg-emerald-800 text-white shadow-sm hover:shadow"
                    : isFull
                        ? "bg-secondary/5 border border-secondary/10 text-secondary/30 cursor-not-allowed shadow-none"
                        : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow"
            }`}
        >
            {isInSession(uri) ? (
                <RiCheckLine className="size-5" />
            ) : (
                <MdOutlineScience className="size-5" />
            )}
            {isInSession(uri)
                ? `View combined search (${count})`
                : isFull
                    ? `Session full`
                    : count > 0
                        ? `Add to session (${count + 1})`
                        : "Combine this ingredient"}
        </button>
    );

    return (
        <PageLayout
            title=" "
            titleHighlight={ingredient.name}
            titleHighlightClassName="bg-gradient-to-r from-emerald-600 to-teal-500"
            gradientClassName="from-emerald-500/[0.04]"
            subtitle="Lists of pharmaceutical products containing this ingredient"
            pageTitle={ingredient.name}
            showSearch={false}
            showGradient={true}
            headerActions={combineButton}
        >
            <div className="flex justify-center gap-2 mt-2 md:justify-start">
                <div className="flex items-center gap-1.5 px-3 py-1 border rounded-full w-fit bg-emerald-50/60 text-emerald-800 text-13 font-text border-emerald-100/80 shadow-sm">
                    <div className="rounded-full bg-emerald-500 size-1.5 animate-pulse"></div>
                    Active Ingredient
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1 border rounded-full w-fit bg-secondary/5 text-secondary/80 text-13 font-text border-secondary/10">
                    <FiSearch className="size-3.5 text-secondary/60" />
                    Found in {pagination.totalItems} Products
                </div>
            </div>

            {/* Recommended Products — Top Brands & Well Reviewed */}
            <div className="w-full mt-8">
                <div className="mb-4">
                    <h2 className="text-20 font-semibold text-secondary">
                        Recommended Products Containing {ingredient.name}
                    </h2>
                </div>

                <Tabs activeTabClassName="bg-emerald-600" tabs={[
                    {
                        label: 'TOP BRANDS',
                        iconType: 'trophy',
                        content: (
                            topBrands.length > 0 ? (
                                <div>
                                    <p className="mb-4 text-14 text-secondary/60 leading-relaxed" style={{ maxWidth: '60ch' }}>
                                        These products come from large, well-established pharmaceutical companies.
                                        They are widely available in pharmacies and hospitals.
                                    </p>
                                    <Carousel>
                                        {topBrands.map((product) => (
                                            <div key={product._id} className="pb-[1rem] px-[0.5rem]">
                                                <TradenameResultCard
                                                    key={product._id}
                                                    product={product}
                                                    currentUri={uri}
                                                    fixedHeight={true}
                                                    fixedWidth={true}
                                                    highlightCardBorder={true}
                                                />
                                            </div>
                                        ))}
                                    </Carousel>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center gap-3 p-8 bg-white border border-secondary/10 shadow-sm rounded-xl text-center max-w-lg mx-auto my-4">
                                    <div className="flex items-center justify-center size-12 rounded-full bg-secondary/5 mb-1 text-secondary/40">
                                        <MdOutlineScience className="size-6" />
                                    </div>
                                    <p className="text-16 font-semibold text-secondary">
                                        No top brands found
                                    </p>
                                    <p className="text-14 text-secondary/60 leading-relaxed" style={{ maxWidth: '45ch' }}>
                                        If you know of a well-known brand that makes a product
                                        with {ingredient.name}, please let us know at{' '}
                                        <a href="mailto:support@pharmacy.wiki" className="text-primary hover:underline font-medium">
                                            support@pharmacy.wiki
                                        </a>{' '}
                                        and we'll be happy to add it.
                                    </p>
                                </div>
                            )
                        )
                    },
                    {
                        label: 'WELL REVIEWED',
                        iconType: 'star',
                        content: (
                            wellReviewed.length > 0 ? (
                                <div>
                                    <p className="mb-4 text-14 text-secondary/60 leading-relaxed" style={{ maxWidth: '60ch' }}>
                                        These products have received positive feedback from people who have used them.
                                        Customer ratings can help you choose a product that works well for others.
                                    </p>
                                    <Carousel>
                                        {wellReviewed.map((product) => (
                                            <div key={product._id} className="pb-[1rem] px-[0.5rem]">
                                                <TradenameResultCard
                                                    key={product._id}
                                                    product={product}
                                                    currentUri={uri}
                                                    fixedHeight={true}
                                                    fixedWidth={true}
                                                    highlightCardBorder={true}
                                                />
                                            </div>
                                        ))}
                                    </Carousel>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-3 items-center justify-center p-8 bg-white border border-secondary/10 shadow-sm rounded-xl text-center max-w-lg mx-auto my-4">
                                    <div className="flex items-center justify-center size-12 rounded-full bg-secondary/5 mb-1 text-secondary/40">
                                        <FiSearch className="size-6" />
                                    </div>
                                    <p className="text-16 font-semibold text-secondary">
                                        No verified customer reviews
                                    </p>
                                    <p className="text-14 text-secondary/60 leading-relaxed" style={{ maxWidth: '45ch' }}>
                                        We don't have customer-reviewed products for this ingredient yet.
                                        Visit{' '}
                                        <a
                                            href="https://pharmacypilot.info?utm_source=pharmacywiki&utm_medium=ingredient&utm_campaign=find_more_certified_store"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-primary hover:underline font-medium"
                                        >
                                            PharmacyPilot.info
                                        </a>{' '}
                                        to find certified stores and products with verified reviews.
                                    </p>
                                </div>
                            )
                        )
                    }
                ]} defaultTab={0} />
            </div>

            {/* All Products List */}
            <div className="mt-8">
                <div className="mb-4">
                    <h2 className="text-18 font-semibold text-secondary">
                        All Products with {ingredient.name}
                    </h2>
                    <p className="mt-1 text-14 text-secondary/60">
                        A complete list of {pagination.totalItems} products containing this ingredient.
                    </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                    {tradenames.map((product, index) => (
                        <TradenameResultCard
                            key={product._id}
                            product={product}
                            currentUri={uri}
                            lastItemRef={index === tradenames.length - 1 ? lastItemRef : null}
                        />
                    ))}
                </div>
            </div>

            {isLoadingMore && (
                <div className="flex justify-center mt-6">
                    <div className="w-8 h-8 border-t-2 border-b-2 rounded-full animate-spin border-primary"></div>
                </div>
            )}

            {pagination.totalPages <= 3 && <SponsersAffiliations hidePadding={true} />}
        </PageLayout>
    );
}
